import { useCallback, useEffect, useMemo, useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { StatCard } from "@project/admin/components/module-ui";
import { AdmissionDetailViewDialog } from "@project/admin/components/admission-detail-view-dialog";
import { AdmissionApplicationForm } from "@project/components/admission-application-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Input } from "@project/components/ui/input";
import { Button } from "@project/components/ui/button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@project/components/ui/alert-dialog";
import { Badge } from "@project/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import { TablePagination } from "@project/admin/components/table-pagination";
import {
  Search,
  Eye,
  ClipboardList,
  Clock,
  CheckCircle2,
  XCircle,
  Plus,
  Loader2,
  Pencil,
} from "lucide-react";
import { projectToast } from "@project/lib/toast";
import { getApiErrorMessage } from "@project/lib/api-error";
import { formatDate } from "@project/lib/date-format";
import { exportApiRowsToCsv } from "@project/lib/csv";
import { CsvExportButton } from "@project/admin/components/csv-export-button";
import { elderAdmissionService } from "@project/services/admin/elder-admission.service";
import { fetchAllListData } from "@project/services/admin/list-api-helpers";
import type { ApiPagination } from "@project/types/location";
import {
  formatAdmissionNumber,
  normalizeAdmissionStatus,
  type ElderAdmissionRecord,
  type ElderAdmissionStats,
  type ElderAdmissionStatus,
} from "@project/types/elder-admission";

type StatusTab = "All" | "Pending" | "Approved" | "Rejected";
type ActionKind = "approve" | "reject";

const TAB_STATUS: Record<Exclude<StatusTab, "All">, string> = {
  Pending: "pending",
  Approved: "approved",
  Rejected: "rejected",
};

const PAGE_SIZE = 20;

const EMPTY_STATS: ElderAdmissionStats = {
  total: 0,
  pending: 0,
  approved: 0,
  rejected: 0,
};

function statusBadge(status: ElderAdmissionStatus) {
  const normalized = normalizeAdmissionStatus(status);
  const map = {
    pending: {
      label: "Pending",
      className: "bg-amber-500/15 text-amber-700 hover:bg-amber-500/15",
    },
    approved: {
      label: "Approved",
      className: "bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15",
    },
    rejected: {
      label: "Rejected",
      className: "bg-rose-500/15 text-rose-700 hover:bg-rose-500/15",
    },
  } as const;
  const item = map[normalized];
  return <Badge className={item.className}>{item.label}</Badge>;
}

const ACTION_GRID_CLASS = "grid w-36 grid-cols-4 items-center gap-0.5";

function AdmissionApplicationActions({
  row,
  showReviewActions,
  onView,
  onEdit,
  onApprove,
  onReject,
}: {
  row: ElderAdmissionRecord;
  showReviewActions: boolean;
  onView: () => void;
  onEdit: () => void;
  onApprove: () => void;
  onReject: () => void;
}) {
  const appNo = formatAdmissionNumber(row);
  const iconClass = "h-8 w-8 shrink-0";

  return (
    <div className={ACTION_GRID_CLASS}>
      <div className="flex justify-center">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={`${iconClass} text-primary`}
          onClick={onView}
          title="Open"
          aria-label={`View ${appNo}`}
        >
          <Eye className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex justify-center">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={iconClass}
          onClick={onEdit}
          title="Edit"
          aria-label={`Edit ${appNo}`}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex justify-center">
        {showReviewActions ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={`${iconClass} text-emerald-600 hover:bg-emerald-500/10 hover:text-emerald-700`}
            onClick={onApprove}
            title="Approve"
            aria-label={`Approve ${appNo}`}
          >
            <CheckCircle2 className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
      <div className="flex justify-center">
        {showReviewActions ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={`${iconClass} text-rose-600 hover:bg-rose-500/10 hover:text-rose-700`}
            onClick={onReject}
            title="Reject"
            aria-label={`Reject ${appNo}`}
          >
            <XCircle className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default function AdmissionApplicationsList() {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [tab, setTab] = useState<StatusTab>("All");
  const [rows, setRows] = useState<ElderAdmissionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<ApiPagination>({
    count: 0,
    current_page: 1,
    total_pages: 1,
    next: null,
    previous: null,
  });
  const [stats, setStats] = useState<ElderAdmissionStats>(EMPTY_STATS);
  const [viewTarget, setViewTarget] = useState<ElderAdmissionRecord | null>(
    null
  );
  const [addOpen, setAddOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<ElderAdmissionRecord | null>(
    null
  );
  const [editLoading, setEditLoading] = useState(false);
  const [editFormKey, setEditFormKey] = useState(0);
  const [actionTarget, setActionTarget] = useState<{
    row: ElderAdmissionRecord;
    kind: ActionKind;
  } | null>(null);
  const [acting, setActing] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);

  const openAddModal = () => {
    setFormKey(key => key + 1);
    setAddOpen(true);
  };

  const openEditModal = async (row: ElderAdmissionRecord) => {
    setEditOpen(true);
    setEditLoading(true);
    setEditRecord(null);
    try {
      const record = await elderAdmissionService.getAdmission(row.id);
      setEditRecord(record ?? row);
      setEditFormKey(key => key + 1);
    } catch (error) {
      projectToast.error(
        getApiErrorMessage(error, "Failed to load admission application.")
      );
      setEditOpen(false);
    } finally {
      setEditLoading(false);
    }
  };

  const closeEditModal = () => {
    if (editLoading) {
      return;
    }
    setEditOpen(false);
    setEditRecord(null);
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQ(q.trim());
      setPage(1);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [q]);

  const fetchRows = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      try {
        const result = await elderAdmissionService.listAdmissionsPage({
          page: targetPage,
          page_size: PAGE_SIZE,
          search: debouncedQ || undefined,
          status: tab === "All" ? undefined : TAB_STATUS[tab],
        });
        setRows(result.data ?? []);
        setPagination(result.pagination);
        setStats(result.stats ?? EMPTY_STATS);
        if (result.pagination.current_page !== targetPage) {
          setPage(result.pagination.current_page);
        }
      } catch (error) {
        projectToast.error(
          getApiErrorMessage(error, "Failed to load admission applications.")
        );
      } finally {
        setLoading(false);
      }
    },
    [debouncedQ, tab]
  );

  useEffect(() => {
    void fetchRows(page);
  }, [page, fetchRows]);

  const handleTabChange = (next: StatusTab) => {
    if (next === tab) {
      return;
    }
    setTab(next);
    setPage(1);
  };

  const exportCsv = async () => {
    setExportingCsv(true);
    try {
      const exportRows = await fetchAllListData(
        query => elderAdmissionService.listAdmissionsPage(query),
        {
          search: debouncedQ || undefined,
          status: tab === "All" ? undefined : TAB_STATUS[tab],
        }
      );
      if (!exportRows.length) {
        projectToast.error("Nothing to export");
        return;
      }
      exportApiRowsToCsv(
        `admission-applications-${new Date().toISOString().slice(0, 10)}`,
        exportRows as Record<string, unknown>[]
      );
      projectToast.success(`Exported ${exportRows.length} applications`);
    } catch (error) {
      projectToast.error(getApiErrorMessage(error, "Failed to export CSV"));
    } finally {
      setExportingCsv(false);
    }
  };

  const confirmAction = async () => {
    if (!actionTarget) {
      return;
    }
    setActing(true);
    try {
      const { row, kind } = actionTarget;
      if (kind === "approve") {
        await elderAdmissionService.approve(row.id);
        projectToast.success(
          `${formatAdmissionNumber(row)} approved successfully.`
        );
      } else {
        await elderAdmissionService.reject(row.id);
        projectToast.success(`${formatAdmissionNumber(row)} rejected.`);
      }
      setActionTarget(null);
      void fetchRows(page);
    } catch (error) {
      projectToast.error(
        getApiErrorMessage(error, "Could not update application status.")
      );
    } finally {
      setActing(false);
    }
  };

  const canApproveReject = (row: ElderAdmissionRecord) =>
    normalizeAdmissionStatus(row.status) === "pending";

  const emptyMessage = useMemo(() => {
    if (debouncedQ) {
      return "No applications match your search.";
    }
    if (tab === "All") {
      return "No admission applications found.";
    }
    return `No ${tab.toLowerCase()} applications.`;
  }, [debouncedQ, tab]);

  return (
    <AdminShell
      title="Admission Applications"
      subtitle="Admission application form and application records"
    >
      <AdmissionDetailViewDialog
        open={!!viewTarget}
        onOpenChange={open => {
          if (!open) {
            setViewTarget(null);
          }
        }}
        admissionId={viewTarget?.id ?? null}
        preview={viewTarget}
      />

      <div className="mb-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Total Applications"
          value={stats.total}
          icon={<ClipboardList className="h-5 w-5" strokeWidth={2.25} />}
          accent="from-sky-600 to-sky-500"
        />
        <StatCard
          label="Pending"
          value={stats.pending}
          icon={<Clock className="h-5 w-5" />}
          accent="from-amber-600 to-amber-500"
        />
        <StatCard
          label="Approved"
          value={stats.approved}
          icon={<CheckCircle2 className="h-5 w-5" />}
          accent="from-emerald-600 to-emerald-500"
        />
        <StatCard
          label="Rejected"
          value={stats.rejected}
          icon={<XCircle className="h-5 w-5" />}
          accent="from-rose-600 to-rose-500"
        />
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-4xl overflow-y-auto rounded-2xl bg-background p-4 pb-6 sm:p-6 sm:pb-8">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              New Admission Application
            </DialogTitle>
          </DialogHeader>
          {addOpen && (
            <AdmissionApplicationForm
              key={formKey}
              formId="admission-application-form"
              mode="admin"
              onSubmitted={() => {
                setAddOpen(false);
                void fetchRows(1);
                setPage(1);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={editOpen}
        onOpenChange={open => {
          if (!open) {
            closeEditModal();
          }
        }}
      >
        <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-4xl overflow-y-auto rounded-2xl bg-background p-4 pb-6 sm:p-6 sm:pb-8">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Edit Admission Application
              {editRecord && (
                <span className="mt-1 block font-mono text-xs font-normal text-muted-foreground">
                  {formatAdmissionNumber(editRecord)}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {editLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            editRecord && (
              <AdmissionApplicationForm
                key={editFormKey}
                formId="admission-application-edit-form"
                mode="admin"
                editingId={editRecord.id}
                initialRecord={editRecord}
                onSubmitted={() => {
                  closeEditModal();
                  void fetchRows(page);
                }}
              />
            )
          )}
        </DialogContent>
      </Dialog>

      <div className="mb-6 mt-10 flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search by app no, elder or applicant…"
            className="pl-8"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {(["All", "Pending", "Approved", "Rejected"] as const).map(s => (
            <button
              key={s}
              type="button"
              onClick={() => handleTabChange(s)}
              className={`rounded-full border px-3 py-1 text-xs transition ${
                tab === s
                  ? "border-primary bg-primary text-primary-foreground"
                  : "hover:border-primary/40 border-border bg-card"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <CsvExportButton onExport={exportCsv} exporting={exportingCsv} />
          <Button
            type="button"
            size="sm"
            className="gap-1 rounded-xl"
            onClick={openAddModal}
          >
            <Plus className="h-4 w-4" /> Add Application
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
        <Table className="min-w-[720px]">
          <TableHeader>
            <TableRow>
              <TableHead>Application Number</TableHead>
              <TableHead>Applicant Name</TableHead>
              <TableHead>Elder Name</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-36">
                <div className="flex justify-end">
                  <span className="block w-36 text-right text-xs font-medium text-muted-foreground">
                    Actions
                  </span>
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="py-10 text-center text-sm text-muted-foreground"
                >
                  <XCircle className="text-muted-foreground/60 mx-auto mb-2 h-5 w-5" />
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              rows.map(row => (
                <TableRow key={row.id}>
                  <TableCell className="font-mono text-xs">
                    {formatAdmissionNumber(row)}
                  </TableCell>
                  <TableCell className="font-medium">
                    {row.applicant_name}
                  </TableCell>
                  <TableCell>{row.elder_name}</TableCell>
                  <TableCell>
                    {row.created_at ? formatDate(row.created_at) : "—"}
                  </TableCell>
                  <TableCell>{statusBadge(row.status)}</TableCell>
                  <TableCell>
                    <div className="flex justify-end">
                      <AdmissionApplicationActions
                        row={row}
                        showReviewActions={canApproveReject(row)}
                        onView={() => setViewTarget(row)}
                        onEdit={() => void openEditModal(row)}
                        onApprove={() =>
                          setActionTarget({ row, kind: "approve" })
                        }
                        onReject={() =>
                          setActionTarget({ row, kind: "reject" })
                        }
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        className="mt-4"
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={pagination.count}
        totalPages={pagination.total_pages}
        loading={loading}
        onPageChange={setPage}
      />

      <AlertDialog
        open={!!actionTarget}
        onOpenChange={open => {
          if (!open && !acting) {
            setActionTarget(null);
          }
        }}
      >
        <AlertDialogContent className="max-w-md rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actionTarget?.kind === "approve"
                ? "Approve application?"
                : "Reject application?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionTarget?.kind === "approve"
                ? "This will mark the admission application as approved. The applicant may be contacted for next steps."
                : "This will mark the admission application as rejected. This action should be used after review."}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {actionTarget && (
            <div className="bg-muted/40 rounded-xl border border-border px-4 py-3 text-sm">
              <p className="font-mono text-xs text-muted-foreground">
                {formatAdmissionNumber(actionTarget.row)}
              </p>
              <p className="mt-1 font-medium text-foreground">
                {actionTarget.row.elder_name}
              </p>
              <p className="text-muted-foreground">
                Applicant: {actionTarget.row.applicant_name}
              </p>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={acting} className="rounded-xl">
              Cancel
            </AlertDialogCancel>
            <Button
              type="button"
              disabled={acting}
              className={`rounded-xl ${
                actionTarget?.kind === "approve"
                  ? "bg-emerald-600 text-white hover:bg-emerald-600/90"
                  : "hover:bg-destructive/90 bg-destructive text-destructive-foreground"
              }`}
              onClick={() => void confirmAction()}
            >
              {acting
                ? "Processing…"
                : actionTarget?.kind === "approve"
                  ? "Approve"
                  : "Reject"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}
